<template>
  <SectionShell
    :icon="meta.icon || 'search'"
    :label="meta.label || fieldKey"
    :count="fieldSlot.values.size"
    :total="total"
    :open="open"
    @toggle="open = $event"
  >
    <div :style="{ fontSize: '10px', color: 'var(--p-surface-400)', padding: '0 2px 6px', fontFamily: mono }">
      {{ decl.match === 'contains' ? 'substring match' : 'exact match' }}
    </div>
    <div v-if="selected.length > 0" :style="{ display: 'flex', flexWrap: 'wrap', gap: '5px', padding: '0 2px 7px' }">
      <span
        v-for="v in selected"
        :key="v"
        :style="{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          background: 'var(--p-highlight-background, color-mix(in srgb, var(--p-primary-500) 14%, transparent))',
          borderRadius: '999px',
          padding: '2px 4px 2px 9px',
          fontSize: '12px',
          fontFamily: mono,
          color: 'var(--p-text-color)',
        }"
      >
        {{ v }}
        <button
          :style="{ background: 'transparent', border: 0, padding: '2px', cursor: 'pointer', color: 'var(--p-surface-400)', display: 'flex' }"
          @click="search.toggleValue(fieldKey, v)"
        >
          <i class="pi pi-times" :style="{ fontSize: '9px' }" />
        </button>
      </span>
    </div>
    <div :style="{ position: 'relative' }">
      <i
        class="pi pi-search"
        :style="{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'var(--p-surface-400)', pointerEvents: 'none' }"
      />
      <input
        v-model="text"
        spellcheck="false"
        :placeholder="`Search ${meta.label || fieldKey}…`"
        :style="{ ...rangeInput, padding: '6px 26px 6px 26px' }"
        @keydown.enter.prevent="commitTyped"
      >
      <button
        v-if="text"
        title="Clear"
        :style="{ position: 'absolute', right: '7px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 0, padding: '2px', cursor: 'pointer', color: 'var(--p-surface-400)', display: 'flex' }"
        @click="text = ''"
      >
        <i class="pi pi-times" :style="{ fontSize: '10px' }" />
      </button>
    </div>

    <div v-if="typing" :style="typingListStyle">
      <div v-if="sug.status === 'loading'" :style="{ padding: '8px 10px', fontSize: '11.5px', color: 'var(--p-surface-400)', display: 'flex', alignItems: 'center', gap: '7px' }">
        <i class="pi pi-spin pi-spinner" :style="{ fontSize: '11px' }" /> Querying…
      </div>
      <div v-else-if="sug.status === 'error'" :style="{ padding: '8px 10px', fontSize: '11.5px', color: 'var(--p-text-muted-color)', display: 'flex', alignItems: 'center', gap: '8px' }">
        <span :style="{ flex: 1 }">Request failed</span>
        <button :style="{ fontSize: '11px', fontWeight: 700, color: 'var(--p-primary-700)', background: 'transparent', border: 0, cursor: 'pointer', fontFamily: 'inherit' }" @click="sug.retry">Retry</button>
      </div>
      <!-- An empty list is not an empty result: Enter still filters by the text, and a `contains` field matches it as a fragment. -->
      <div v-else-if="sug.status === 'empty'" :style="{ padding: '8px 10px', fontSize: '11.5px', color: 'var(--p-surface-400)' }">
        No suggestions · press Enter to filter by “{{ text.trim() }}”
      </div>
      <template v-else-if="sug.status === 'ready'">
        <button
          v-for="v in sug.values"
          :key="v"
          :style="{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', border: 0, borderTop: '1px solid var(--p-content-border-color)', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }"
          @click="search.toggleValue(fieldKey, v)"
        >
          <Check :on="fieldSlot.values.has(v)" />
          <span :style="{ fontSize: '12.5px', color: 'var(--p-text-color)', fontFamily: mono }">{{ v }}</span>
        </button>
      </template>
    </div>
    <div v-else :style="{ marginTop: '5px' }">
      <FacetRows :facet="facet" :field-slot="fieldSlot" :field-key="fieldKey" :meta="meta" :search="search" />
    </div>
  </SectionShell>
</template>

<script setup>
// String field — hybrid "search within" box: faceted list when empty, typeahead
// when typing. Ported from fql-search.jsx StringSection.
import { ref, computed } from 'vue'
import { useValueSuggestions, useFacetValues } from '@/admin-collection-layout/composables/useStructuredSearch.js'
import { mono, rangeInput, listScroll, Check } from './bits.js'
import SectionShell from './SectionShell.vue'
import FacetRows from './FacetRows.vue'

// SearchSidebar hands every section the same prop bag. This one reads no knownValues,
// and `inheritAttrs: false` keeps the undeclared binding off SectionShell's
// root element rather than letting it render there as an attribute.
defineOptions({ name: 'StringSection', inheritAttrs: false })

const props = defineProps({
  fieldKey: { type: String, required: true },
  decl: { type: Object, default: () => ({}) },
  fieldSlot: { type: Object, required: true },
  meta: { type: Object, default: () => ({}) },
  search: { type: Object, required: true },
  fieldDefinitions: { type: Object, required: true },
  valueProvider: { type: Function, default: null },
  facetProvider: { type: Function, default: null },
  debounceMs: { type: Number, default: 220 },
})

const open = ref(false)
const text = ref('')
const typing = computed(() => text.value.trim().length > 0)

const sug = useValueSuggestions({
  field: () => props.fieldKey,
  prefix: () => text.value,
  fieldDefinitions: () => props.fieldDefinitions,
  valueProvider: props.valueProvider,
  limit: () => 30,
  enabled: () => open.value && typing.value,
  debounceMs: () => props.debounceMs,
})
const facet = useFacetValues({
  field: () => props.fieldKey,
  query: () => props.search.query,
  facetProvider: props.facetProvider,
  enabled: () => open.value && !typing.value,
  debounceMs: () => props.debounceMs,
})
// Enter filters by what was typed, the same as clicking it in the list would. A
// field that offers no values has no list to click, so without this its box
// could not filter at all. On a `contains` field the text is a fragment.
function commitTyped() {
  const value = text.value.trim()
  if (value === '') return
  if (!props.fieldSlot.values.has(value)) props.search.toggleValue(props.fieldKey, value)
  text.value = ''
}
const selected = computed(() => [...props.fieldSlot.values])
const total = computed(() => {
  if (typing.value || (facet.status !== 'ready' && facet.status !== 'loading')) return null
  const s = new Set(facet.values.map((o) => o.value))
  props.fieldSlot.values.forEach((v) => s.add(v))
  return s.size
})
const typingListStyle = computed(() => ({
  marginTop: '5px',
  border: '1px solid var(--p-content-border-color)',
  borderRadius: '9px',
  overflow: 'hidden',
  background: 'var(--p-content-background)',
  ...(sug.status === 'ready' ? listScroll : {}),
}))
</script>
